import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Divider
} from '@mui/material';
import { CheckCircle, Error, Email, Send } from '@mui/icons-material';
import config from '../../Config';

const VerifyEmail = () => {
    // Статусы: loading, success, error, waiting, manual
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [manualError, setManualError] = useState('');
    const [userId, setUserId] = useState(null);
    const [isSending, setIsSending] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const fromRegistration = location.state?.fromRegistration;
    const fromLogin = location.state?.fromLogin;
    const fromBanner = location.state?.fromBanner;
    const userEmail = location.state?.email || '';

    useEffect(() => {
        const verifyToken = async () => {
            const params = new URLSearchParams(location.search);
            const token = params.get('token');

            // Если токена нет, но переход произошёл с другой страницы – ждём верификации
            if (!token && (fromRegistration || fromLogin || fromBanner)) {
                setStatus('waiting');
                if (userEmail) {
                    setManualEmail(userEmail);
                }
                return;
            }
            if (!token) {
                setStatus('manual');
                return;
            }

            try {
                const response = await fetch(`${config.apiBaseUrl}/user/verify-email?token=${token}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();

                if (response.ok) {
                    setStatus('success');
                    setMessage(data.message || 'Email verified successfully');

                    // Обновляем localStorage
                    const userData = JSON.parse(localStorage.getItem('user'));
                    if (userData) {
                        userData.user_info.email_verified = true;
                        localStorage.setItem('user', JSON.stringify(userData));
                    }

                    // Диспатчим событие, чтобы уведомить другие компоненты
                    window.dispatchEvent(new Event('emailVerified'));
                } else {
                    setStatus('error');
                    setMessage(data.detail || 'Failed to verify email');
                }
            } catch (error) {
                setStatus('error');
                setMessage('An error occurred during verification');
                console.error('Email verification error:', error);
            }
        };

        verifyToken();
    }, [location.search, fromRegistration, fromLogin, fromBanner, userEmail]);

    const findUserAndSendVerification = async () => {
        if (!manualEmail) {
            setManualError('Please enter your email address');
            return;
        }

        setIsSending(true);
        setManualError('');

        try {
            const findResponse = await fetch(`${config.apiBaseUrl}/user/find-by-login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ login: manualEmail })
            });

            if (!findResponse.ok) {
                setManualError('User not found with this email address');
                setIsSending(false);
                return;
            }

            const userData = await findResponse.json();
            if (!userData.id) {
                setManualError('User account not found');
                setIsSending(false);
                return;
            }

            if (userData.email_verified) {
                setManualError('Email is already verified. You can log in now.');
                setIsSending(false);
                return;
            }

            const verifyResponse = await fetch(`${config.apiBaseUrl}/user/resend-verification/${userData.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (verifyResponse.ok) {
                setStatus('waiting');
                setUserId(userData.id);
            } else {
                const errorData = await verifyResponse.json();
                setManualError(errorData.detail || 'Failed to send verification email');
            }
        } catch (error) {
            console.error('Error sending verification:', error);
            setManualError('An error occurred while sending the verification email');
        } finally {
            setIsSending(false);
        }
    };

    const resendVerification = async () => {
        if (!userId && !userEmail) {
            setStatus('manual');
            return;
        }

        setIsSending(true);

        try {
            let id = userId;
            if (!id && userEmail) {
                const findResponse = await fetch(`${config.apiBaseUrl}/user/find-by-login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ login: userEmail })
                });

                if (findResponse.ok) {
                    const data = await findResponse.json();
                    id = data.id;
                    setUserId(id);
                } else {
                    throw new Error('Could not find user');
                }
            }

            if (id) {
                const response = await fetch(`${config.apiBaseUrl}/user/resend-verification/${id}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to send verification');
                }

                setMessage('Verification email sent! Please check your inbox.');
            }
        } catch (error) {
            console.error('Error resending verification:', error);
            setMessage('Failed to resend verification email. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const goToLogin = () => {
        navigate('/login');
    };

    const goToHome = () => {
        navigate('/');
    };

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: '#f5f5f5'
        }}>
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 500,
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: 2
                }}
            >
                {status === 'loading' && (
                    <>
                        <CircularProgress size={60} sx={{ mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Verifying your email...
                        </Typography>
                        <Typography color="text.secondary">
                            Please wait while we verify your email address.
                        </Typography>
                    </>
                )}

                {status === 'waiting' && (
                    <>
                        <Email sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Please Verify Your Email
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                            We've sent a verification email to <strong>{userEmail || manualEmail}</strong>.
                            Please check your inbox and spam folder.
                        </Alert>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            You need to verify your email before you can log in.
                            Click the link in the email we sent you.
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={resendVerification}
                                sx={{ mr: 2 }}
                                disabled={isSending}
                                startIcon={isSending ? <CircularProgress size={16} /> : null}
                            >
                                {isSending ? "Sending..." : "Resend Email"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={goToLogin}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </>
                )}

                {status === 'manual' && (
                    <>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Verify Your Email
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Please enter your email address to request a verification link:
                        </Typography>
                        <TextField
                            fullWidth
                            label="Email Address"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            error={!!manualError}
                            helperText={manualError}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={findUserAndSendVerification}
                            disabled={isSending}
                            startIcon={isSending ? <CircularProgress size={16} /> : <Send />}
                            sx={{ mb: 2 }}
                        >
                            {isSending ? "Sending..." : "Send Verification Email"}
                        </Button>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={goToLogin}
                                sx={{ mr: 1 }}
                            >
                                Back to Login
                            </Button>
                            <Button
                                variant="text"
                                onClick={goToHome}
                            >
                                Go to Home
                            </Button>
                        </Box>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Email Verified!
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            {message}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={goToLogin}
                                sx={{ mr: 2 }}
                            >
                                Log In
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={goToHome}
                            >
                                Go to Home
                            </Button>
                        </Box>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Verification Failed
                        </Typography>
                        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                            {message}
                        </Alert>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            The verification link may have expired or is invalid.
                            Would you like to request a new verification email?
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setStatus('manual')}
                                sx={{ mr: 2 }}
                            >
                                Request New Verification
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={goToLogin}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default VerifyEmail;